import { parse, stringify } from 'qs';
import { LogModule } from '@/data/config';

import { isBrowser } from '@/modules/detector';
import { getBrowser } from '@/modules/utils';
import { loggerWarn } from '@/modules/logger';
import { getUtilsConfig } from '@/modules/config';
import { isHasAllOwnProperty, isObject, isString } from '@/modules/validate';

/**
 * 地址模式
 * prefix: 添加前缀的方式 - passportdaily.hstong.com域名
 * path: 通过路径域名方式 - daily.vbkr.com/passport域名
 */
export enum UrlMode {
  Prefix = 'Prefix',
  Path = 'Path',
}

export enum ENV {
  Dev = 'dev',
  Daily = 'daily',
  Feature = 'feature',

  Beta = 'beta',
  Uat = 'uat',

  Prod = 'prod',
}

export enum Platform {
  US = 'US',
  HST = 'HST',
  SA = 'SA',
  SG = 'SG',
  NZ = 'NZ',
  MY = 'MY',
}

/**
 * 适配url参数，默认获取当前浏览器地址
 * 兼容服务端
 * @param url
 * @returns
 */
export function getUrl(url?: string) {
  const _url = url ?? (isBrowser() ? getBrowser().location.href : '');
  return _url;
}

export function getParseUrl(url?: string) {
  const _url = getUrl(url);
  if (!_url) return;
  try {
    const data = new URL(_url);
    return data;
  } catch (e) {
    loggerWarn('URL解析失败参数为：' + _url, LogModule.Url);
    return;
  }
}

/**
 * 获取当前hostName
 * @param {boolean} isIgnorePort 是否忽略端口
 * @returns
 */
export function getHost(url?: string, isIgnorePort = true) {
  const data = getParseUrl(url);
  if (!data) return;

  return isIgnorePort ? data.hostname : data.host;
}

/**
 * 获取一级域名, 忽略端口号
 * @param host
 * @returns
 */
export function getDomain(url?: string): string {
  const reg = /^[\d.]*$/;
  const host = getHost(url);
  if (!host) {
    loggerWarn('getDomain方法错误' + url, LogModule.Url);
    return '';
  }
  if (reg.test(host) || host === 'localhost') return '';
  // safari 不适配 正向后行零宽断言
  // const result = host.match(/((?<=\.).+?(?=(:|\/))|(?<=\.).+)/i);
  // return result && result.length ? result[0] : '';
  return host.replace(/([\w|-]+\.)/i, '');
}

/**
 * 获取当前的环境
 * 返回daily/beta/prod/dev/feature/uat
 */
export function getEnv(url?: string, defaultEnv = ENV.Prod): ENV {
  const host = getHost(url);
  if (!host) {
    loggerWarn('getEnv方法错误', LogModule.Url);
    return defaultEnv;
  }

  // 目前没有dev测试环境
  const testENV = [ENV.Daily, ENV.Feature, ENV.Uat, ENV.Beta];

  const config = getUtilsConfig();

  // 判断是否是dev测试环境
  if (config.hasDevEnv) {
    testENV.push(ENV.Dev);
  }

  const regx = new RegExp(testENV.join('|'), 'i');

  const result = host.match(regx);
  // 默认生产标识
  if (!result) return defaultEnv;
  return result[0] as ENV;
}

/**
 * @param env ENV
 * @param isCDN CDN环境 理解为处本地开发环境和线上生产环境之外的环境
 * @param prodText 生产环境特定返回字符串
 * @param separator 分隔符 rdaily 或者 r-daily
 * @returns
 */
export function formatterEnv(
  env: ENV,
  options?: {
    isCDN?: boolean;
    prodText?: string;
    separator?: string;
  }
) {
  const _options = {
    isCDN: false,
    ...options,
  };
  const config = getUtilsConfig();
  const _separator = _options?.separator || '';

  if (env === ENV.Prod) return _options.prodText ?? _options.isCDN ? '' : 'www';
  if (env !== ENV.Dev) return _separator + env;

  // env = dev有两种情况
  // 1. 测试环境的env
  // 2. 本地开发的env

  // 如果测试环境的env, 则返回dev, dev.vbkr.com环境
  if (config.hasDevEnv) return _separator + ENV.Dev;

  // 默认是本地环境，则直接去公共配置的devENV, 默认daily
  return _separator + config.devENV;
}

/**
 * 客户端，服务端
 * 公共Url拼接链接参数
 * @param {string} url
 * @param {object} query
 * @returns {string}
 */
export function getUrlByParam(url: string, query?: Record<string, unknown>) {
  if (!isObject(query)) return url;

  let isPath = false;
  if (isString(url) && /^\//.test(url)) {
    isPath = true;
    url = 'http://www.vbkr.com' + url;
  }

  const data = getParseUrl(url);

  /** 解析失败 */
  if (!data) {
    // if ( && isObject(query)) {
    //   /** 直接拼接后缀 */
    //   console.log(url);
    //   debugger;
    // }
    return url;
  }

  const _query = {
    ...(data?.search ? parse(data.search.replace(/^\?/, '')) : {}),
    ...query,
  };

  if (isPath) {
    return `${data.pathname}?${stringify(_query)}${data.hash}`;
  }

  return `${data.origin}${data.pathname}?${stringify(_query)}${data.hash}`;
}

/**
 * 客户端 服务端
 * 获取当前地址，默认取客户端链接
 * @param {Object} context (可选)服务端执行上下文
 */
export function getHref<T = Record<any, any>>(context?: T): string {
  if (isBrowser()) {
    return getBrowser().location.href;
  }

  if (isObject(context) && isHasAllOwnProperty(['href'], context) && isString(context?.href))
    return context.href;

  if (isObject(context) && isHasAllOwnProperty(['req'], context)) {
    const { req } = context;

    /** 验证是否存在 */
    if (
      isObject(req) &&
      isObject(req?.headers) &&
      isString(req?.headers?.host) &&
      isString(req?.originalUrl)
    ) {
      return `https://${req.headers?.host}${req.originalUrl}`;
    }
  }

  loggerWarn('getHref方法，服务端，未传入context', LogModule.Url);
  return '';
}

/**
 * 根据环境或者页面链接获取资源域名 r.xxxx.com
 * @param options
 * @returns 例如 https://r.hstong.com / https://r.alsahm.com
 */
export function getResDomain(options: {
  platform?: Platform;
  env?: ENV;
  debugEnv?: ENV;
  hasDevEnv?: boolean;
  url?: string;
  ssl?: boolean;
  protocol?: string;
}) {
  const globalConfig = getUtilsConfig();
  const defaultEnv = ENV.Prod;
  const gEnv = options?.env ?? (globalConfig.env || defaultEnv);
  const gHasDevEnv = options?.hasDevEnv ?? globalConfig.hasDevEnv;
  const debugEnv = options?.debugEnv ?? (globalConfig.devENV || defaultEnv);
  const platform = options?.platform ?? globalConfig.platform;
  const url = options?.url;
  let env = getEnv(url, gEnv);
  // 处于本地开发时，使用指定的调试环境
  env = !gHasDevEnv && gEnv === ENV.Dev ? debugEnv : env;
  const isHTTPS = options?.ssl ?? true;
  // 浏览器请求地址协议可以跟随当前页面地址的协议
  const protocol = options?.protocol ?? (isBrowser() ? '' : `http${isHTTPS ? 's' : ''}:`);
  if (platform === Platform.SA) {
    return `${protocol}//${globalConfig?.resChildHost}${formatterEnv(env, {
      isCDN: true,
      separator: '-',
    })}.alsahm.com`;
  }
  if (platform === Platform.US) {
    return `${protocol}//r${formatterEnv(env, { isCDN: true })}.vbrokersus.com`;
  }
  if (platform === Platform.SG) {
    return `${protocol}//r${formatterEnv(env, { isCDN: true })}.tickrs.com.sg`;
  }
  if (platform === Platform.MY) {
    const domain = env === ENV.Prod ? 'global.mplusonline.com' : `mplusglobal.com.my`;
    return `${protocol}//r${formatterEnv(env, { isCDN: true })}.${domain}`;
  }
  return `${protocol}//r${formatterEnv(env, { isCDN: true })}.hstong.com`;
}

/**
 * 获取各展业公共资源地址
 * @param pathFileName
 * @param options
 * @returns 例如：沙特展业：https://r-daily.alsahm.com/fe/hs-sa-res/
 */
export function getCdnCommonResUrl(
  pathFileName: string,
  options?: {
    context?: Record<string | number, unknown>;
    platform?: Platform;
    env?: ENV;
    debugEnv?: ENV;
    hasDevEnv?: boolean;
    url?: string;
    ssl?: boolean;
  }
) {
  const platform = options?.platform ?? getUtilsConfig().platform;
  let path = '';
  if (platform === Platform.SA) {
    path = '/fe/hs-sa-res';
  } else if (platform === Platform.US) {
    path = '/fe/hs-us-res';
  } else if (platform === Platform.SG) {
    path = '/fe/hs-sg-res';
  } else if (platform === Platform.MY) {
    path = '/fe/hs-my-res';
  } else {
    path = 'v2/commons';
  }
  const currentUrl = getHref(options?.context);
  pathFileName = pathFileName.startsWith('/') ? pathFileName : '/' + pathFileName;
  return (
    getResDomain({
      url: currentUrl,
      ...options,
    }) +
    path +
    pathFileName
  );
}

/**
 * 改变当前 Url 地址参数
 * @param opts 参数 { params: 参数对象, isReplace: 是否替换方式, isRefresh: 是否刷新, url: 地址, context: 服务端执行上下文, mode: 'update' | 'delete' | 'append' }
 * @returns
 */
export function changeURLParam(
  opts: {
    params?: Record<string, any>;
    isReplace?: boolean;
    isRefresh?: boolean;
    url?: string;
    context?: any;
    mode?: 'update' | 'delete' | 'append';
  } = {}
): void {
  const {
    params = {},
    isReplace = true,
    isRefresh = false,
    url,
    context,
    mode = 'append',
  } = { ...opts };

  const oldUrl = url || getHref(context);
  const urlData = getParseUrl(oldUrl);
  if (!urlData) return;
  const urlParams = urlData.searchParams;
  Object.keys(params).forEach(key => {
    if (mode === 'update') urlParams.set(key, params[key]);
    else if (mode === 'delete') urlParams.delete(key);
    else {
      if (urlParams.has(key)) urlParams.set(key, params[key]);
      else urlParams.append(key, params[key]);
    }
  });
  const newUrl = urlData.toString();
  if (isRefresh) window.location.href = newUrl;
  else if (isReplace) history.replaceState({ path: newUrl }, '', newUrl);
  else history.pushState({ path: newUrl }, '', newUrl);
}
